/**
 * Custom hook for managing footer data
 * Provides cached access to footer content from Strapi
 */

import { useEffect, useState } from 'react';

import {
  fetchFooterData,
  FooterData,
  FooterSection,
  SocialLink,
} from '@/services';

interface UseFooterDataReturn {
  footerData: FooterData | null;
  loading: boolean;
  error: string | null;
  // Transformed footer data
  companyName: string;
  companyDescription: string;
  contactInfo?: {
    address: string;
    phone: string;
    email: string;
  };
  socialLinks: SocialLink[];
  footerSections: FooterSection[];
  copyright: string;
  isActive: boolean;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage footer data from Strapi
 * @returns Object with footer data, loading state, error state, and refetch function
 */
export function useFooterData(): UseFooterDataReturn {
  const [footerData, setFooterData] = useState<FooterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch footer data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchFooterData();
      setFooterData(data);
    } catch (err) {
      console.error('Error fetching footer data:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to fetch footer data'
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      fetchData();
    } else {
      // In SSR, just set loading to false
      setLoading(false);
    }
  }, []);

  return {
    footerData,
    loading,
    error,
    companyName: footerData?.companyName ?? '',
    companyDescription: footerData?.companyDescription ?? '',
    contactInfo: footerData?.contactInfo
      ? {
          address: footerData.contactInfo.address,
          phone: footerData.contactInfo.phone,
          email: footerData.contactInfo.email,
        }
      : undefined,
    socialLinks: footerData?.socialLinks ?? [],
    footerSections: footerData?.footerLinks ?? [],
    copyright: footerData?.copyright ?? '',
    isActive: footerData?.isActive ?? false,
    refetch: fetchData,
  };
}
